/**
 * Default imagery for the Game Pass live preview.
 *
 * The in-app Game Pass screen pulls these icons from its own Next `public/`
 * folder (and, in prod, a per-brand asset manifest). Those paths are not served
 * by the PAM's CloudFront distribution, so the defaults are bundled into the
 * PAM's own `public/images/game-pass-preview/` (copied 1:1 from
 * trivelta-gamepass-frontend) and served as same-origin static assets. This
 * keeps the preview rendering regardless of the CDN/env configuration.
 *
 * These are only the *defaults*: any asset the admin uploads in the Assets
 * section overrides the matching slot (see {@link resolvePreviewImage}) so the
 * preview reflects their changes live.
 */

import { type AssetKey } from "../AssetsSection/assetsSectionData";

const PUBLIC_BASE = (process.env.PUBLIC_URL ?? "").replace(/\/+$/, "");

/** Resolves a same-origin static asset path under the app's public folder. */
const asset = (file: string): string =>
  `${PUBLIC_BASE}/images/game-pass-preview/${file}`;

/** Default (trivelta) imagery shown until an admin uploads a replacement. */
export const GAME_PASS_PREVIEW_IMAGES = {
  /** Yellow cash coin — the first "Rewards" tile (`cash` asset slot). */
  cash: asset("cash_yellow_icon.png"),
  /** Yellow game-coin — the second "Rewards" tile (`coins` asset slot). */
  coins: asset("coin_yellow_icon.png"),
  /** League / challenge badge shown on the rail + hero header. */
  challengeBadge: asset("ic_gp_challenge1.png"),
  /** Orange trophy that caps the progress rail and the Rewards card. */
  trophy: asset("progress_end_orange_icon.png"),
  /** "Season concluded" screen shown when no season is currently live. */
  seasonConcluded: asset("season_concluded.png"),
  /** Diagonal-lines / carbon-fibre page texture (transparent PNG) laid under
   *  the season screen, mirroring the mobile game pass page background
   *  (`GAME_PASS_LINES_TEXTURE` in trivelta-gamepass-frontend's battlePassStyle). */
  background: asset("game_pass_background.png"),
} as const;

/**
 * Maps an editable asset slot to its default image, so a slot the admin hasn't
 * uploaded to still shows the trivelta default in the preview. The Challenge
 * Complete slot is the orange trophy (`progress_end_orange_icon`) that caps
 * the progress rail, mirroring the consumer app; slots without a natural place
 * on this single-challenge mock fall back to the challenge badge.
 */
export const ASSET_FALLBACK_IMAGE: Record<AssetKey, string> = {
  cash: GAME_PASS_PREVIEW_IMAGES.cash,
  coins: GAME_PASS_PREVIEW_IMAGES.coins,
  challengeComplete: GAME_PASS_PREVIEW_IMAGES.trophy,
  profitBoost: GAME_PASS_PREVIEW_IMAGES.challengeBadge,
  freeCashPick: GAME_PASS_PREVIEW_IMAGES.challengeBadge,
  depositMatch: GAME_PASS_PREVIEW_IMAGES.challengeBadge,
  freeSpins: GAME_PASS_PREVIEW_IMAGES.challengeBadge,
  seasonConcluded: GAME_PASS_PREVIEW_IMAGES.seasonConcluded,
  // The carbon-fibre slot drives the page background texture (see
  // GamePassPreview's `--gp-page-texture`), so it resolves through here and its
  // default is the bundled page texture. The remaining background / leaderboard
  // slots have no place in this single-challenge preview mock and only need a
  // valid fallback to satisfy the total record.
  carbonFibreBackground: GAME_PASS_PREVIEW_IMAGES.background,
  gamePassCompletedBackground: GAME_PASS_PREVIEW_IMAGES.seasonConcluded,
  gamePassOptInBackground: GAME_PASS_PREVIEW_IMAGES.background,
  leaderboardNoRank: GAME_PASS_PREVIEW_IMAGES.challengeBadge,
  leaderboardFirstPlace: GAME_PASS_PREVIEW_IMAGES.challengeBadge,
  leaderboardSecondPlace: GAME_PASS_PREVIEW_IMAGES.challengeBadge,
  leaderboardThirdPlace: GAME_PASS_PREVIEW_IMAGES.challengeBadge,
};

/**
 * Resolves the image to show for an editable asset slot, in priority order:
 * the admin's uploaded file (via its object URL), the TCM-manifest default
 * (see `gamePassDefaultAssets`), then the bundled trivelta default.
 */
export const resolvePreviewImage = (
  key: AssetKey,
  objectUrls: Partial<Record<AssetKey, string>>,
  defaultUrls?: Partial<Record<AssetKey, string>>,
): string =>
  objectUrls[key] ?? defaultUrls?.[key] ?? ASSET_FALLBACK_IMAGE[key];
